import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, BedDouble } from "lucide-react";
import "./PropertyCard.css";

export default function PropertyCard({ property }) {
  // ── Image ──
  const image =
    Array.isArray(property.images) && property.images.length > 0
      ? property.images[0]
      : null;

  // ── Title ──
  const title = property.title || "Untitled Project";

  // ── Developer ──
  const developer = property.developer || null;

  // ── Location ──
  const location = [property.locality, property.city]
    .filter(Boolean)
    .join(", ") || property.address || null;

  // ── Configurations from units ──
  const configs = Array.isArray(property.units)
    ? [...new Set(property.units.map((u) => u.unitType).filter(Boolean))]
    : [];

  // ── Price ──
  // 1. Prefer lowest price from units
  // 2. Fallback to top-level "Price"
  let priceLabel = null;

  if (Array.isArray(property.units) && property.units.length > 0) {
    const prices = property.units
      .map((u) => Number(u.price))
      .filter((p) => !isNaN(p) && p > 0);

    if (prices.length > 0) {
      priceLabel = formatPrice(Math.min(...prices));
    }
  }

  // Fallback to top-level Price (string)
  if (!priceLabel && property.Price) {
    priceLabel = formatPrice(Number(property.Price));
  }

  // ── Possession ──
  const possession = property.possessionStatus || null;

  return (
    <Link
      to={`/property/${property.id}`}
      className="property-card group block overflow-hidden"
    >
      {/* Image */}
      <div className="property-card-image">
        {image ? (
          <img src={image} alt={title} loading="lazy" />
        ) : (
          <div className="property-card-placeholder">No image</div>
        )}

        {/* Badges */}
        <div className="property-card-badges">
          {property.category && (
            <span className="badge badge-category">{property.category}</span>
          )}
          {property.featured && (
            <span className="badge badge-featured">Featured</span>
          )}
          {property.exclusive && (
            <span className="badge badge-exclusive">Exclusive</span>
          )}
          {property.luxurious && (
            <span className="badge badge-luxurious">Luxury</span>
          )}
        </div>

        <div className="property-card-gradient" />
      </div>

      {/* Content */}
      <div className="property-card-body">
        <div className="property-card-header">
          <div>
            <h3 className="property-card-title">{title}</h3>
            {developer && (
              <p className="property-card-builder">by {developer}</p>
            )}
          </div>
          <ArrowUpRight className="property-card-arrow" />
        </div>

        {/* Location */}
        {location && (
          <div className="property-card-location">
            <MapPin className="h-4 w-4" />
            <span>{location}</span>
          </div>
        )}

        {/* Configurations */}
        {configs.length > 0 && (
          <div className="property-card-configs">
            {configs.slice(0, 3).map((c) => (
              <span key={c} className="config-chip">
                <BedDouble className="h-3 w-3" />
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Price + Possession */}
        <div className="property-card-footer">
          <div>
            <div className="footer-label">Starting</div>
            <div className="footer-value">
              {priceLabel || "Price on Request"}
            </div>
          </div>

          {possession && (
            <div className="text-right">
              <div className="footer-label">Possession</div>
              <div className="footer-value-sm">{possession}</div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ─── Format price ─── */
function formatPrice(num) {
  if (!num || isNaN(num)) return null;

  if (num >= 10000000) {
    // Crore
    return `₹${(num / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
  }
  if (num >= 100000) {
    // Lakh
    return `₹${(num / 100000).toFixed(2).replace(/\.00$/, "")} L`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
}